import { ChevronDown, ChevronRight, Check } from 'lucide-react';

// Flatten tree to an array of { node, depth, hasChildren, isExpanded, parentIds }
function flatten(roots, expanded) {
  const out = [];
  const stack = [];
  for (let i = roots.length - 1; i >= 0; i--) stack.push({ node: roots[i], depth: 0 });
  while (stack.length) {
    const { node, depth } = stack.pop();
    const children = node.children || [];
    const hasChildren = children.length > 0;
    const isExpanded = !!expanded[node.id];
    out.push({ node, depth, hasChildren, isExpanded });
    if (hasChildren && isExpanded) {
      for (let i = children.length - 1; i >= 0; i--) stack.push({ node: children[i], depth: depth + 1 });
    }
  }
  return out;
}

export default function TreeList({ roots, expanded, toggle, canEdit, dragNode, dropTarget, onDragStart, onDragOver, onDragLeave, onDrop, onPersonClick, bulkMode, bulkSelected }) {
  const rows = flatten(roots, expanded);
  return (
    <div>
      {rows.map(({ node, depth, hasChildren, isExpanded }) => {
        const initials = (node.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
        const outletLower = (node.outlet || '').toLowerCase();
        const isCostCentre = outletLower.indexOf('hq') >= 0 || outletLower.indexOf('common area') >= 0;
        const isDropping = dropTarget === node.id;
        const isDragging = dragNode && dragNode.id === node.id;
        const isSelected = bulkMode && bulkSelected && bulkSelected[node.id];
        const rowStyle = {
          marginLeft: depth * 20,
          backgroundColor: isDropping ? '#ECFDF5' : isSelected ? '#EFF6FF' : 'transparent',
          border: isDropping ? '2px dashed #2D8B5E' : isSelected ? '2px solid #185FA5' : '2px dashed transparent',
        };
        return (
          <div
            key={node.id}
            draggable={canEdit}
            onDragStart={(e) => onDragStart(e, node)}
            onDragOver={(e) => onDragOver(e, node)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, node)}
            className={'flex items-center gap-2 py-1.5 px-2 rounded-md my-0.5 transition-all ' + (canEdit ? 'cursor-grab ' : '') + (isDragging ? 'opacity-40' : '')}
            style={rowStyle}
            data-testid={'reftree-node-' + node.id}
          >
            {hasChildren ? (
              <button onClick={() => toggle(node.id)} className="w-4 h-4 flex items-center justify-center rounded hover:bg-gray-100" style={{ color: '#6B7280' }}>
                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            {bulkMode ? (
              <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isSelected ? '#185FA5' : '#fff', border: '1.5px solid ' + (isSelected ? '#185FA5' : '#CBD5E1') }}>
                {isSelected ? <Check size={12} color="#fff" /> : null}
              </span>
            ) : node.photo_url ? (
              <img src={node.photo_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: '#1B2A4A', color: '#F7F5F2' }}>
                {initials}
              </div>
            )}
            <button onClick={() => onPersonClick && onPersonClick(node)} className="flex-1 text-left min-w-0 hover:opacity-70">
              <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{node.name}</p>
              <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>
                {node.designation || node.role}
                {node.outlet ? <span style={{ color: isCostCentre ? '#D97706' : '#9CA3AF' }}> · {node.outlet}</span> : null}
              </p>
            </button>
            {hasChildren ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                {(node.children || []).length}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
